var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Validation-rule detail / edit / create pane (CZNA-245).
 *
 * Drives both flows off one component:
 *  - edit: loads the rule via `useValidationRule`, seeds the form once.
 *  - create: `mode="create"` — blank MANUAL rule, POST on save.
 *
 * Editable fields: name, message, expression (MVL editor), appliesToRecordType,
 * eventTriggers, severityOverrides, enabled, documentation. `code`/`source`/
 * audit fields are read-only. ALEPH_CONVERTED rules are editable but cannot be
 * deleted — the delete action disables them instead (backend returns 409).
 *
 * Save is gated client-side by the `write` permission (UX only — server's 403
 * is the real gate) and by MVL validity reported from the editor.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, Controller } from 'react-hook-form';
import { useStripes } from '@folio/stripes/core';
import { Pane, PaneFooter, Button, TextField, TextArea, Checkbox, Select, KeyValue, Loading, MessageBanner, AccordionSet, Accordion, ConfirmationModal, } from '@folio/stripes/components';
import MvlExpressionEditor from './MvlExpressionEditor';
import RuleSourceBadge from './RuleSourceBadge';
import RuleHistoryView from './RuleHistoryView';
import { useValidationRule, useValidationRuleMeta, useSaveValidationRule, useDeleteValidationRule } from '../../hooks/useValidationRules';
import { getErrorInfo } from '../../utils/getErrorCode';
import { getErrorKind } from '../../utils/errorKind';
var WRITE_PERM = 'czna-authorities.validation.rules.write';
var toFormValues = function (rule) {
    var _a, _b, _c, _d;
    return ({
        code: rule.code,
        name: rule.name,
        message: rule.message,
        expression: rule.expression,
        appliesToRecordType: (_a = rule.appliesToRecordType) !== null && _a !== void 0 ? _a : [],
        eventTriggers: (_b = rule.eventTriggers) !== null && _b !== void 0 ? _b : [],
        severityOverrides: __assign({}, ((_c = rule.severityOverrides) !== null && _c !== void 0 ? _c : {})),
        enabled: rule.enabled,
        documentation: (_d = rule.documentation) !== null && _d !== void 0 ? _d : '',
    });
};
var EMPTY_FORM = {
    code: '',
    name: '',
    message: '',
    expression: '',
    appliesToRecordType: [],
    eventTriggers: [],
    severityOverrides: {},
    enabled: true,
    documentation: '',
};
/** Strip form-internal shape into the API save body (drops empty severity pins). */
var buildSaveBody = function (values) {
    var severityOverrides = {};
    Object.entries(values.severityOverrides).forEach(function (_a) {
        var event = _a[0], sev = _a[1];
        if (sev)
            severityOverrides[event] = sev;
    });
    return {
        code: values.code,
        name: values.name,
        message: values.message,
        expression: values.expression,
        appliesToRecordType: values.appliesToRecordType,
        eventTriggers: values.eventTriggers,
        severityOverrides: severityOverrides,
        enabled: values.enabled,
        documentation: values.documentation.trim() || null,
    };
};
var ValidationRuleDetail = function (_a) {
    var _b, _c, _d, _e;
    var id = _a.id, mode = _a.mode, onClose = _a.onClose, onSaved = _a.onSaved, onDeleted = _a.onDeleted, onOpenInChecker = _a.onOpenInChecker;
    var intl = useIntl();
    var stripes = useStripes();
    var isCreate = mode === 'create';
    var canWrite = stripes.hasPerm(WRITE_PERM);
    var _f = useValidationRule(isCreate ? undefined : id), rule = _f.data, isLoading = _f.isLoading, isError = _f.isError;
    var meta = useValidationRuleMeta().data;
    var _g = useState(true), mvlValid = _g[0], setMvlValid = _g[1];
    var _h = useState(null), saveError = _h[0], setSaveError = _h[1];
    var _j = useState(false), conflict = _j[0], setConflict = _j[1];
    var _k = useState(false), showDeleteModal = _k[0], setShowDeleteModal = _k[1];
    var _l = useState(false), historyOpen = _l[0], setHistoryOpen = _l[1];
    var seededRef = useRef(false);
    var _m = useForm({
        defaultValues: EMPTY_FORM,
        mode: 'onChange',
    }), control = _m.control, handleSubmit = _m.handleSubmit, reset = _m.reset, _o = _m.formState, errors = _o.errors, isDirty = _o.isDirty;
    // Seed the form from server data exactly once (never on background refetch —
    // ui-czna-development rule). Create mode starts blank.
    useEffect(function () {
        if (isCreate) {
            if (!seededRef.current) {
                seededRef.current = true;
                reset(EMPTY_FORM);
            }
            return;
        }
        if (rule && !seededRef.current) {
            seededRef.current = true;
            reset(toFormValues(rule));
        }
    }, [rule, isCreate, reset]);
    // Reset the seed guard when the selected rule changes (parent reuses pane).
    useEffect(function () {
        seededRef.current = false;
    }, [id, isCreate]);
    var saveMutation = useSaveValidationRule({
        onSuccess: function (saved) {
            setSaveError(null);
            setConflict(false);
            onSaved(saved);
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _a.sent();
                        // CZNA-1625 — no answer in time: the save may have happened.
                        if (getErrorKind(info) === 'timeout') {
                            setSaveError(intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }));
                            return [2 /*return*/];
                        }
                        if (info.status === 409) {
                            setConflict(true);
                            return [2 /*return*/];
                        }
                        // 400 carries MVL compile diagnostics; surface the backend message.
                        setSaveError(info.message || intl.formatMessage({ id: 'ui-czna-authorities.validationRules.save.error' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var deleteMutation = useDeleteValidationRule({
        onSuccess: function () { return onDeleted === null || onDeleted === void 0 ? void 0 : onDeleted(); },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var info;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        info = _a.sent();
                        // CZNA-1625 — no answer in time: the rule may have been deleted; do not fall back to disabling it.
                        if (getErrorKind(info) === 'timeout') {
                            setSaveError(intl.formatMessage({ id: 'ui-czna-authorities.error.timeout' }));
                            return [2 /*return*/];
                        }
                        // ALEPH_CONVERTED → 409: fall back to disable (PUT enabled=false).
                        if (info.status === 409 && rule) {
                            saveMutation.mutate({
                                mode: 'update',
                                id: rule.id,
                                body: __assign(__assign({}, buildSaveBody(__assign(__assign({}, toFormValues(rule)), { enabled: false }))), { expectedUpdatedAt: rule.updatedAt }),
                            });
                            return [2 /*return*/];
                        }
                        setSaveError(info.message || intl.formatMessage({ id: 'ui-czna-authorities.validationRules.delete.error' }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var onSubmit = function (values) {
        setSaveError(null);
        var body = buildSaveBody(values);
        if (isCreate) {
            saveMutation.mutate({ mode: 'create', body: body });
        }
        else if (rule) {
            saveMutation.mutate({ mode: 'update', id: rule.id, body: __assign(__assign({}, body), { expectedUpdatedAt: rule.updatedAt }) });
        }
    };
    var handleConfirmDelete = function () {
        setShowDeleteModal(false);
        if (rule)
            deleteMutation.mutate(rule.id);
    };
    if (!isCreate && isLoading) {
        return (React.createElement(Pane, { defaultWidth: "50%", dismissible: true, onClose: onClose, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.detail.loading" }) },
            React.createElement(Loading, null)));
    }
    if (!isCreate && (isError || !rule)) {
        return (React.createElement(Pane, { defaultWidth: "50%", dismissible: true, onClose: onClose, paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.detail.title" }) },
            React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.detail.loadError" }))));
    }
    var recordTypeOptions = ((_b = meta === null || meta === void 0 ? void 0 : meta.recordTypes) !== null && _b !== void 0 ? _b : []).map(function (t) { return ({
        value: t,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(t) }),
    }); });
    var eventOptions = (_c = meta === null || meta === void 0 ? void 0 : meta.events) !== null && _c !== void 0 ? _c : ['CREATE_DRAFT', 'UPDATE_DRAFT', 'SUBMIT_FOR_REVIEW', 'PUBLISH', 'MONTHLY_CHECK', 'IMPORT_JOB'];
    var severities = (_d = meta === null || meta === void 0 ? void 0 : meta.severities) !== null && _d !== void 0 ? _d : ['BLOCKING', 'WARNING'];
    var paneTitle = isCreate
        ? intl.formatMessage({ id: 'ui-czna-authorities.validationRules.create.title' })
        : ((rule === null || rule === void 0 ? void 0 : rule.name) || intl.formatMessage({ id: 'ui-czna-authorities.validationRules.detail.title' }));
    var saveDisabled = !canWrite || saveMutation.isLoading || !mvlValid || (!isCreate && !isDirty);
    var footer = (React.createElement(PaneFooter, { renderEnd: (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, type: "submit", form: "validation-rule-form", disabled: saveDisabled },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.action.save" }))), renderStart: (React.createElement(Button, { buttonStyle: "default", marginBottom0: true, onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.action.cancel" }))) }));
    return (React.createElement(Pane, { defaultWidth: "50%", dismissible: true, onClose: onClose, paneTitle: paneTitle, paneSub: rule ? rule.code : undefined, footer: canWrite ? footer : undefined },
        !canWrite && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.detail.readOnly" }))),
        conflict && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.save.conflict" }))),
        saveError && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return setSaveError(null); } }, saveError)),
        rule && (React.createElement("div", { style: { display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' } },
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.code" }), value: rule.code }),
            React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.source" }), value: React.createElement(RuleSourceBadge, { source: rule.source }) }),
            rule.alephOrigin && (React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.alephOrigin" }), value: rule.alephOrigin })))),
        React.createElement("form", { id: "validation-rule-form", onSubmit: handleSubmit(onSubmit) },
            isCreate && (React.createElement(Controller, { name: "code", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.validation.code.required' }) }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.code' }), error: (_b = errors.code) === null || _b === void 0 ? void 0 : _b.message, required: true, disabled: !canWrite })));
                } })),
            React.createElement(Controller, { name: "name", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.validation.name.required' }) }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement(TextField, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.name' }), error: (_b = errors.name) === null || _b === void 0 ? void 0 : _b.message, required: true, disabled: !canWrite })));
                } }),
            React.createElement(Controller, { name: "message", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.validation.message.required' }) }, render: function (_a) {
                    var _b;
                    var field = _a.field;
                    return (React.createElement(TextArea, __assign({}, field, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.message' }), error: (_b = errors.message) === null || _b === void 0 ? void 0 : _b.message, required: true, disabled: !canWrite })));
                } }),
            React.createElement("div", { style: { marginBottom: '1rem' } },
                React.createElement("label", { htmlFor: "mvl-editor", style: { display: 'block', fontWeight: 500, marginBottom: '4px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.expression" }),
                    ' *'),
                React.createElement(Controller, { name: "expression", control: control, rules: { required: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.validation.expression.required' }) }, render: function (_a) {
                        var field = _a.field;
                        return (React.createElement(MvlExpressionEditor, { testId: "mvl-editor", value: field.value, onChange: field.onChange, onValidityChange: setMvlValid, readOnly: !canWrite }));
                    } }),
                ((_e = errors.expression) === null || _e === void 0 ? void 0 : _e.message) && (React.createElement("div", { style: { color: '#d00', fontSize: '0.85em' } }, errors.expression.message))),
            !isCreate && rule && onOpenInChecker && (React.createElement("div", { style: { marginBottom: '1rem' } },
                React.createElement(Button, { marginBottom0: true, onClick: function () { return onOpenInChecker(rule); }, "data-testid": "open-in-marc-checker" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.action.openInChecker" })))),
            React.createElement("fieldset", { style: { border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px', marginBottom: '1rem' } },
                React.createElement("legend", { style: { fontWeight: 500, padding: '0 4px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.appliesToRecordType" })),
                React.createElement(Controller, { name: "appliesToRecordType", control: control, render: function (_a) {
                        var field = _a.field;
                        // "ALL" is a master sentinel meaning "every authority type": it is
                        // mutually exclusive with individual types. When on, the individual
                        // checkboxes render checked + disabled (visual cue that ALL governs
                        // them) and the stored value collapses to ["ALL"]; when off, the
                        // individual types are independently selectable. This prevents the
                        // contradictory ["ALL","PERSONAL"] state.
                        var allSelected = field.value.includes('ALL');
                        var allOption = recordTypeOptions.find(function (o) { return String(o.value) === 'ALL'; });
                        var individualOptions = recordTypeOptions.filter(function (o) { return String(o.value) !== 'ALL'; });
                        return (React.createElement(React.Fragment, null,
                            allOption && (React.createElement("div", { style: { paddingBottom: '6px', marginBottom: '6px', borderBottom: '1px solid #e0e0e0' } },
                                React.createElement(Checkbox, { label: allOption.label, checked: allSelected, disabled: !canWrite, onChange: function () { return field.onChange(allSelected ? [] : ['ALL']); } }))),
                            React.createElement("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '2px', opacity: allSelected ? 0.5 : 1 } }, individualOptions.map(function (opt) { return (React.createElement(Checkbox, { key: opt.value, label: opt.label, checked: allSelected || field.value.includes(opt.value), disabled: !canWrite || allSelected, onChange: function () {
                                    var next = field.value.includes(opt.value)
                                        ? field.value.filter(function (v) { return v !== opt.value; })
                                        : __spreadArray(__spreadArray([], field.value, true), [opt.value], false);
                                    field.onChange(next);
                                } })); }))));
                    } })),
            React.createElement("fieldset", { style: { border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px', marginBottom: '1rem' } },
                React.createElement("legend", { style: { fontWeight: 500, padding: '0 4px' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.field.eventTriggers" }),
                    ' *'),
                React.createElement("p", { style: { margin: '0 0 8px', color: '#666', fontSize: '0.85em' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.validationRules.hint.eventTriggers" })),
                React.createElement(Controller, { name: "eventTriggers", control: control, rules: { validate: function (v) { return (v && v.length > 0) || intl.formatMessage({ id: 'ui-czna-authorities.validationRules.validation.events.required' }); } }, render: function (_a) {
                        var eventsField = _a.field;
                        return (React.createElement(Controller, { name: "severityOverrides", control: control, render: function (_a) {
                                var _b;
                                var sevField = _a.field;
                                return (React.createElement("div", null,
                                    eventOptions.map(function (event) {
                                        var _a, _b;
                                        var checked = eventsField.value.includes(event);
                                        // The "default" choice = no override → the module-wide default
                                        // severity for this event applies. Show the resolved value
                                        // (e.g. "Výchozí — Blokující") so the choice is unambiguous (CZNA-294).
                                        var defaultSeverity = (_a = meta === null || meta === void 0 ? void 0 : meta.severityDefaults) === null || _a === void 0 ? void 0 : _a[event];
                                        var inheritLabel = defaultSeverity
                                            ? intl.formatMessage({ id: 'ui-czna-authorities.validationRules.severity.inheritResolved' }, { severity: intl.formatMessage({ id: "ui-czna-authorities.validationRules.severity.".concat(defaultSeverity) }) })
                                            : intl.formatMessage({ id: 'ui-czna-authorities.validationRules.severity.inherit' });
                                        return (React.createElement("div", { key: event, style: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' } },
                                            React.createElement("div", { style: { minWidth: '180px' } },
                                                React.createElement(Checkbox, { label: intl.formatMessage({ id: "ui-czna-authorities.validationRules.event.".concat(event) }), checked: checked, disabled: !canWrite, onChange: function () {
                                                        eventsField.onChange(checked
                                                            ? eventsField.value.filter(function (v) { return v !== event; })
                                                            : __spreadArray(__spreadArray([], eventsField.value, true), [event], false));
                                                    } })),
                                            checked && (React.createElement("div", { style: { minWidth: '160px' } },
                                                React.createElement(Select, { marginBottom0: true, disabled: !canWrite, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.severityOverride' }), value: (_b = sevField.value[event]) !== null && _b !== void 0 ? _b : '', dataOptions: __spreadArray([
                                                        { value: '', label: inheritLabel }
                                                    ], severities.map(function (s) { return ({
                                                        value: s,
                                                        label: intl.formatMessage({ id: "ui-czna-authorities.validationRules.severity.".concat(s) }),
                                                    }); }), true), onChange: function (e) {
                                                        var next = __assign({}, sevField.value);
                                                        if (e.target.value)
                                                            next[event] = e.target.value;
                                                        else
                                                            delete next[event];
                                                        sevField.onChange(next);
                                                    } })))));
                                    }),
                                    ((_b = errors.eventTriggers) === null || _b === void 0 ? void 0 : _b.message) && (React.createElement("div", { style: { color: '#d00', fontSize: '0.85em' } }, errors.eventTriggers.message))));
                            } }));
                    } })),
            React.createElement(Controller, { name: "enabled", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(Checkbox, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.enabled' }), checked: field.value, disabled: !canWrite, onChange: function (e) { return field.onChange(e.target.checked); } }));
                } }),
            React.createElement(Controller, { name: "documentation", control: control, render: function (_a) {
                    var field = _a.field;
                    return (React.createElement(TextArea, __assign({}, field, { value: field.value || '', label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.field.documentation' }), disabled: !canWrite })));
                } })),
        !isCreate && rule && canWrite && (React.createElement("div", { style: { marginTop: '1rem' } },
            React.createElement(Button, { buttonStyle: "danger", marginBottom0: true, onClick: function () { return setShowDeleteModal(true); }, disabled: deleteMutation.isLoading },
                React.createElement(FormattedMessage, { id: rule.source === 'ALEPH_CONVERTED'
                        ? 'ui-czna-authorities.validationRules.action.disable'
                        : 'ui-czna-authorities.validationRules.action.delete' })))),
        !isCreate && rule && (React.createElement(AccordionSet, null,
            React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.history.title' }), open: historyOpen, onToggle: function () { return setHistoryOpen(function (o) { return !o; }); }, id: "rule-history-accordion" },
                React.createElement(RuleHistoryView, { ruleId: rule.id, enabled: historyOpen })))),
        React.createElement(ConfirmationModal, { open: showDeleteModal, heading: intl.formatMessage({
                id: (rule === null || rule === void 0 ? void 0 : rule.source) === 'ALEPH_CONVERTED'
                    ? 'ui-czna-authorities.validationRules.confirmDisable.heading'
                    : 'ui-czna-authorities.validationRules.confirmDelete.heading',
            }), message: intl.formatMessage({
                id: (rule === null || rule === void 0 ? void 0 : rule.source) === 'ALEPH_CONVERTED'
                    ? 'ui-czna-authorities.validationRules.confirmDisable.message'
                    : 'ui-czna-authorities.validationRules.confirmDelete.message',
            }), onConfirm: handleConfirmDelete, onCancel: function () { return setShowDeleteModal(false); }, confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.validationRules.action.confirm' }), isConfirmButtonDisabled: deleteMutation.isLoading, buttonStyle: "danger" })));
};
export default ValidationRuleDetail;
